import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Package, AlertCircle, Box, Truck, Warehouse, Wrench,
  Coins, PackageCheck, TrendingUp, ArrowRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  LineChart, Line, PieChart, Pie, Cell, Legend,
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GHS = (n) => `GHS ${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const NUM = (n) => Number(n || 0).toLocaleString();

const PIPELINE_COLORS = {
  pending: '#f59e0b',
  picking: '#8b5cf6',
  packed: '#00a89c',
  shipped: '#1680e5',
  cancelled: '#94a3b8',
};

const TypeBadge = ({ type }) => (
  <span
    className={
      'inline-block px-2 py-0.5 rounded-full text-xs font-medium ' +
      (type === 'internal' ? 'bg-teal-50 text-teal-700' : 'bg-blue-50 text-blue-700')
    }
    data-testid={`type-badge-${type}`}
  >
    {type === 'internal' ? 'Internal' : 'Customer'}
  </span>
);

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [internal, setInternal] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const [s, i, a] = await Promise.all([
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/dashboard/internal-stats`),
        axios.get(`${API}/dashboard/analytics`),
      ]);
      setStats(s.data);
      setInternal(i.data);
      setAnalytics(a.data);
    } catch (e) {
      toast.error('Failed to load dashboard stats');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="dashboard-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const hero = analytics?.hero || {};
  const chartCatData = (analytics?.value_by_category || []).map((c) => ({
    name: c.category.length > 22 ? c.category.slice(0, 20) + '…' : c.category,
    value: c.value,
    type: c.inventory_type,
  }));
  const movementData = (analytics?.movement_trend || []).map((m) => ({
    date: m.date.slice(5), // MM-DD
    inbound: m.inbound,
    outbound: m.outbound,
  }));
  const pipelineData = (analytics?.order_pipeline || [])
    .filter((p) => p.count > 0)
    .map((p) => ({ name: p.status, value: p.count, fill: PIPELINE_COLORS[p.status] || '#94a3b8' }));

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900">Dashboard</h1>
          <p className="text-slate-600 mt-2">Overview of your warehouse operations</p>
        </div>
        <Link
          to="/analytics"
          className="text-sm font-medium text-[#1680e5] hover:underline flex items-center gap-1"
          data-testid="link-analytics"
        >
          Open full analytics <ArrowRight className="h-4 w-4" />
        </Link>
      </div>

      {/* HERO KPI STRIP */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="border-slate-200 shadow-sm" data-testid="hero-total-value">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Total stock value</CardTitle>
            <div className="bg-blue-50 p-2 rounded-lg">
              <Coins className="h-5 w-5 text-[#1680e5]" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-slate-900">{GHS(hero.total_stock_value)}</div>
            <p className="text-xs text-slate-500 mt-1">
              Customer {GHS(hero.total_stock_value_customer)} · Internal {GHS(hero.total_stock_value_internal)}
            </p>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm" data-testid="hero-items-on-hand">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Total items on hand</CardTitle>
            <div className="bg-teal-50 p-2 rounded-lg">
              <PackageCheck className="h-5 w-5 text-[#00a89c]" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-slate-900">{NUM(hero.total_items_on_hand)}</div>
            <p className="text-xs text-slate-500 mt-1">
              Across {NUM(stats?.total_warehouses)} warehouse{(stats?.total_warehouses || 0) === 1 ? '' : 's'}
            </p>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm" data-testid="hero-low-stock">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Low-stock alerts</CardTitle>
            <div className="bg-orange-50 p-2 rounded-lg">
              <AlertCircle className="h-5 w-5 text-orange-600" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-slate-900">{NUM(hero.total_low_stock)}</div>
            <p className="text-xs text-slate-500 mt-1">
              {NUM(stats?.low_stock_items)} customer · {NUM(internal?.low_stock_internal)} internal
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Value by Category */}
        <Card className="lg:col-span-2 border-slate-200" data-testid="chart-value-by-category">
          <CardHeader>
            <CardTitle className="text-base">Stock value by category</CardTitle>
          </CardHeader>
          <CardContent className="h-72">
            {chartCatData.length === 0 ? (
              <EmptyState label="No inventory yet" />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartCatData} layout="vertical" margin={{ left: 20, right: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis type="number" tick={{ fill: '#64748b', fontSize: 11 }} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
                  <YAxis type="category" dataKey="name" tick={{ fill: '#334155', fontSize: 12 }} width={140} />
                  <Tooltip formatter={(v) => GHS(v)} contentStyle={{ borderRadius: 6, border: '1px solid #e2e8f0' }} />
                  <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                    {chartCatData.map((entry, idx) => (
                      <Cell key={idx} fill={entry.type === 'internal' ? '#00a89c' : '#1680e5'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Order pipeline donut */}
        <Card className="border-slate-200" data-testid="chart-order-pipeline">
          <CardHeader>
            <CardTitle className="text-base">Order pipeline</CardTitle>
          </CardHeader>
          <CardContent className="h-72">
            {pipelineData.length === 0 ? (
              <EmptyState label="No orders yet" />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pipelineData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={45}
                    outerRadius={80}
                    paddingAngle={2}
                  >
                    {pipelineData.map((e, i) => <Cell key={i} fill={e.fill} />)}
                  </Pie>
                  <Tooltip />
                  <Legend verticalAlign="bottom" height={24} wrapperStyle={{ fontSize: 12, textTransform: 'capitalize' }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Movement trend */}
      <Card className="border-slate-200" data-testid="chart-movement-trend">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-[#1680e5]" />
            Stock movement — last 14 days
          </CardTitle>
        </CardHeader>
        <CardContent className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={movementData} margin={{ left: 6, right: 20, top: 6 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="date" tick={{ fill: '#64748b', fontSize: 11 }} />
              <YAxis tick={{ fill: '#64748b', fontSize: 11 }} />
              <Tooltip contentStyle={{ borderRadius: 6, border: '1px solid #e2e8f0' }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line type="monotone" dataKey="inbound" stroke="#00a89c" strokeWidth={2.5} dot={{ r: 3 }} name="Inbound" />
              <Line type="monotone" dataKey="outbound" stroke="#ef4444" strokeWidth={2.5} dot={{ r: 3 }} name="Outbound" />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Top low-stock watchlist */}
      <Card className="border-slate-200" data-testid="table-top-low-stock">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">Top low-stock items</CardTitle>
          <Link to="/inventory" className="text-xs font-medium text-[#1680e5] hover:underline">View inventory</Link>
        </CardHeader>
        <CardContent>
          {(analytics?.top_low_stock || []).length === 0 ? (
            <p className="text-slate-500 text-sm py-6 text-center">Nothing below reorder point. 🎉</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-slate-500 border-b border-slate-200">
                  <tr>
                    <th className="text-left py-2 pr-4 font-medium">SKU</th>
                    <th className="text-left py-2 pr-4 font-medium">Name</th>
                    <th className="text-left py-2 pr-4 font-medium">Type</th>
                    <th className="text-right py-2 pr-4 font-medium">On hand</th>
                    <th className="text-right py-2 pr-4 font-medium">Reorder pt</th>
                    <th className="text-right py-2 font-medium text-orange-600">Shortage</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.top_low_stock.map((row) => (
                    <tr key={row.sku} className="border-b border-slate-100" data-testid={`row-low-${row.sku}`}>
                      <td className="py-2 pr-4 font-mono text-xs">{row.sku}</td>
                      <td className="py-2 pr-4">{row.name}</td>
                      <td className="py-2 pr-4"><TypeBadge type={row.inventory_type} /></td>
                      <td className="py-2 pr-4 text-right">{NUM(row.quantity)}</td>
                      <td className="py-2 pr-4 text-right text-slate-500">{NUM(row.reorder_point)}</td>
                      <td className="py-2 text-right font-semibold text-orange-600">−{NUM(row.shortage)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Compact secondary stats */}
      <div>
        <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <Package className="h-5 w-5 text-[#1680e5]" />
          Operations at a glance
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <MiniStat label="Customer SKUs" value={stats?.total_products} icon={Package} tint="blue" />
          <MiniStat label="Internal SKUs" value={internal?.total_internal_items} icon={Wrench} tint="teal" />
          <MiniStat label="Pending" value={stats?.pending_orders} icon={Box} tint="amber" />
          <MiniStat label="Picking" value={stats?.picking_orders} icon={Box} tint="violet" />
          <MiniStat label="Ready to ship" value={stats?.packed_orders} icon={Truck} tint="teal" />
          <MiniStat label="Warehouses" value={stats?.total_warehouses} icon={Warehouse} tint="slate" />
        </div>
      </div>

      {/* Quick Actions */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle>Quick actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <QuickLink to="/inventory" title="Customer Inventory" sub="Manage customer products" icon={Package} accent="blue" testid="quick-action-inventory" />
            <QuickLink to="/internal-inventory" title="Internal Materials" sub="Manage spare parts & tools" icon={Wrench} accent="teal" testid="quick-action-internal" />
            <QuickLink to="/pick-list" title="Start Picking" sub="View pending pick lists" icon={Box} accent="blue" testid="quick-action-pick" />
            <QuickLink to="/orders" title="View Orders" sub="Track order status" icon={Truck} accent="blue" testid="quick-action-orders" />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function EmptyState({ label }) {
  return (
    <div className="flex items-center justify-center h-full text-slate-400 text-sm">{label}</div>
  );
}

function MiniStat({ label, value, icon: Icon, tint }) {
  const tints = {
    blue: { bg: 'bg-blue-50', text: 'text-[#1680e5]' },
    teal: { bg: 'bg-teal-50', text: 'text-[#00a89c]' },
    amber: { bg: 'bg-orange-50', text: 'text-orange-600' },
    violet: { bg: 'bg-purple-50', text: 'text-purple-600' },
    slate: { bg: 'bg-slate-100', text: 'text-slate-600' },
  }[tint] || { bg: 'bg-slate-50', text: 'text-slate-600' };
  return (
    <div
      className="bg-white border border-slate-200 rounded-lg p-4 flex items-center gap-3"
      data-testid={`mini-stat-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className={`${tints.bg} p-2 rounded-lg`}>
        <Icon className={`h-4 w-4 ${tints.text}`} />
      </div>
      <div>
        <p className="text-xs text-slate-500 leading-tight">{label}</p>
        <p className="text-lg font-semibold text-slate-900 leading-tight">{NUM(value)}</p>
      </div>
    </div>
  );
}

function QuickLink({ to, title, sub, icon: Icon, accent, testid }) {
  const border = accent === 'teal' ? 'hover:border-[#00a89c] hover:bg-teal-50' : 'hover:border-[#1680e5] hover:bg-blue-50';
  const iconColor = accent === 'teal' ? 'text-[#00a89c]' : 'text-[#1680e5]';
  return (
    <Link
      to={to}
      className={`p-4 border border-slate-200 rounded-lg transition-all ${border}`}
      data-testid={testid}
    >
      <Icon className={`h-6 w-6 ${iconColor} mb-2`} />
      <h3 className="font-medium text-slate-900">{title}</h3>
      <p className="text-sm text-slate-600 mt-1">{sub}</p>
    </Link>
  );
}
